package com.example.positionahead

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.lifecycle.viewmodel.compose.viewModel
import com.example.positionahead.ui.screens.*
import com.example.positionahead.ui.theme.PositionAheadTheme
import com.example.positionahead.viewmodel.PlanTradePrefill
import com.example.positionahead.viewmodel.PortfolioViewModel

enum class AppTab(val label: String, val icon: ImageVector, val inBottomNav: Boolean = true) {
    Home("Home", Icons.Default.Home),
    Portfolio("Portfolio", Icons.Default.List),
    Analyze("Analyze", Icons.Default.Info),
    Ideas("Ideas", Icons.Default.Search),
    Plan("Plan", Icons.Default.Star),
    Settings("Settings", Icons.Default.Settings, inBottomNav = false),
    Add("Add", Icons.Default.Add, inBottomNav = false),
    Playbook("Playbook", Icons.Default.Refresh, inBottomNav = false),
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
@Preview
fun App() {
    PositionAheadTheme {
        val viewModel: PortfolioViewModel = viewModel { PortfolioViewModel() }
        val uiState by viewModel.uiState.collectAsState()
        var selectedTab by remember { mutableStateOf(AppTab.Home) }
        var exploreSymbol by remember { mutableStateOf<String?>(null) }
        var planTradePrefill by remember { mutableStateOf<PlanTradePrefill?>(null) }
        var portfolioSellHint by remember { mutableStateOf<String?>(null) }
        var expandSettingsGoals by remember { mutableStateOf(false) }

        LaunchedEffect(selectedTab) {
            when (selectedTab) {
                AppTab.Home, AppTab.Portfolio -> viewModel.refreshRecommendations()
                AppTab.Plan -> viewModel.refreshQuotes()
                else -> Unit
            }
        }

        Scaffold(
            topBar = {
                if (selectedTab.inBottomNav) {
                    TopAppBar(
                        title = {
                            Text(
                                text = selectedTab.label,
                                style = MaterialTheme.typography.titleLarge,
                                fontWeight = FontWeight.ExtraBold
                            )
                        },
                        actions = {
                            IconButton(onClick = { selectedTab = AppTab.Add }) {
                                Icon(Icons.Default.Add, contentDescription = "Add")
                            }
                            IconButton(onClick = { selectedTab = AppTab.Settings }) {
                                Icon(Icons.Default.Settings, contentDescription = "Settings")
                            }
                        }
                    )
                }
            },
            bottomBar = {
                NavigationBar(
                    containerColor = MaterialTheme.colorScheme.surface,
                    tonalElevation = 0.dp
                ) {
                    AppTab.entries.filter { it.inBottomNav }.forEach { tab ->
                        NavigationBarItem(
                            icon = { Icon(tab.icon, contentDescription = tab.label) },
                            label = { 
                                Text(
                                    text = tab.label,
                                    style = MaterialTheme.typography.labelSmall,
                                    fontWeight = if (selectedTab == tab) FontWeight.Bold else FontWeight.Normal
                                ) 
                            },
                            selected = selectedTab == tab,
                            onClick = { selectedTab = tab },
                            colors = NavigationBarItemDefaults.colors(
                                selectedIconColor = MaterialTheme.colorScheme.primary,
                                unselectedIconColor = MaterialTheme.colorScheme.onSurfaceVariant,
                                indicatorColor = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.5f)
                            )
                        )
                    }
                }
            }
        ) { padding ->
            AnimatedContent(
                targetState = selectedTab,
                transitionSpec = {
                    fadeIn().togetherWith(fadeOut())
                },
                modifier = Modifier.padding(padding).fillMaxSize(),
                label = "TabTransition"
            ) { tab ->
                when (tab) {
                    AppTab.Home -> HomeScreen(
                        uiState = uiState,
                        onNavigateToPortfolio = { symbol ->
                            selectedTab = AppTab.Portfolio
                        },
                        onNavigateToDiscover = {
                            selectedTab = AppTab.Ideas
                        },
                        onNavigateToPlan = { selectedTab = AppTab.Plan },
                        onNavigateToAdd = { selectedTab = AppTab.Add },
                        modifier = Modifier.fillMaxSize()
                    )
                    AppTab.Portfolio -> DashboardScreen(
                        uiState = uiState,
                        planSellHintSymbol = portfolioSellHint,
                        onDismissPlanSellHint = { portfolioSellHint = null },
                        onNavigateToPlan = { selectedTab = AppTab.Plan },
                        onRefresh = { viewModel.refreshQuotes() },
                        onSelectAccount = viewModel::selectPortfolio,
                        onCreateAccount = viewModel::createAccount,
                        onUpdateAccount = viewModel::updateAccount,
                        onDeleteAccount = viewModel::deleteAccount,
                        onRemovePosition = viewModel::removePosition,
                        onAddLot = viewModel::addLot,
                        onUpdateLot = viewModel::updateLot,
                        onSellLot = viewModel::sellLot,
                        onDismissAlerts = viewModel::dismissTriggeredAlerts,
                        modifier = Modifier.fillMaxSize(),
                    )
                    AppTab.Analyze -> AnalyzeScreen(
                        uiState = uiState,
                        onFindIdeasForGap = { gap ->
                            selectedTab = AppTab.Ideas
                        },
                        onNavigateToPlan = { selectedTab = AppTab.Plan },
                        modifier = Modifier.fillMaxSize()
                    )
                    AppTab.Ideas -> DiscoverScreen(
                        discoverSections = uiState.discoverSections,
                        watchlistSymbols = uiState.watchlist.map { it.item.symbol }.toSet(),
                        onWatch = viewModel::addToWatchlist,
                        onExplore = { symbol ->
                            exploreSymbol = symbol
                            selectedTab = AppTab.Add
                        },
                        chartSymbol = uiState.chartSymbol,
                        chartRangeDays = uiState.chartRangeDays,
                        chartHistory = uiState.chartHistory,
                        chartQuote = uiState.chartQuote,
                        chartPerformance = uiState.chartPerformance,
                        symbolSuggestions = uiState.chartSymbolSuggestions,
                        onSymbolChange = viewModel::selectChartSymbol,
                        onRangeChange = viewModel::selectChartRange,
                        modifier = Modifier.fillMaxSize(),
                    )
                    AppTab.Plan -> PlanScreen(
                        uiState = uiState,
                        onOpenSettingsGoals = {
                            expandSettingsGoals = true
                            selectedTab = AppTab.Settings
                        },
                        onOpenPlaybook = { selectedTab = AppTab.Playbook },
                        onSavePlanSlot = viewModel::savePlanSlotAsPortfolio,
                        onRefreshPlan = { viewModel.refreshQuotes() },
                        onSelectPlanSlot = viewModel::setPlanCompareSlot,
                        onBuyTrade = { prefill ->
                            planTradePrefill = prefill
                            exploreSymbol = prefill.symbol
                            portfolioSellHint = null
                            selectedTab = AppTab.Add
                        },
                        onSellTrade = { symbol ->
                            portfolioSellHint = symbol
                            planTradePrefill = null
                            selectedTab = AppTab.Portfolio
                        },
                        modifier = Modifier.fillMaxSize(),
                    )
                    AppTab.Playbook -> SimulationScreen(
                        uiState = uiState,
                        onUpdateCapital = viewModel::updateAvailableCapital,
                        onNavigateToPlan = { selectedTab = AppTab.Plan },
                        modifier = Modifier.fillMaxSize(),
                    )
                    AppTab.Add -> AddHubScreen(
                        existingSymbols = uiState.existingSymbols,
                        portfolios = uiState.portfolios,
                        activePortfolioId = uiState.activePortfolioId,
                        initialSymbol = planTradePrefill?.symbol ?: exploreSymbol,
                        initialDollarAmount = planTradePrefill?.dollarAmount,
                        sellHintSymbol = null,
                        quotes = uiState.quotes,
                        importErrors = uiState.importErrors,
                        importSuccessCount = uiState.importSuccessCount,
                        onAddPurchase = viewModel::addPurchase,
                        onDone = {
                            exploreSymbol = null
                            planTradePrefill = null
                            selectedTab = AppTab.Portfolio
                        },
                        onImport = viewModel::importCsv,
                        onClearImportStatus = viewModel::clearImportStatus,
                        onLookupQuote = viewModel::lookupQuote,
                        modifier = Modifier.fillMaxSize(),
                    )
                    AppTab.Settings -> SettingsScreen(
                        apiKey = uiState.apiKey,
                        usesLiveQuotes = uiState.usesLiveQuotes,
                        expandGoalsOnOpen = expandSettingsGoals,
                        onGoalsExpanded = { expandSettingsGoals = false },
                        onSaveApiKey = viewModel::saveApiKey,
                        alerts = uiState.alerts,
                        onCreateAlert = viewModel::createAlert,
                        onDeleteAlert = viewModel::deleteAlert,
                        onToggleAlert = viewModel::toggleAlert,
                        onClearTriggeredAlert = viewModel::clearTriggeredAlert,
                        goal = uiState.goal,
                        currentPortfolioValue = uiState.summary.totalMarketValue,
                        onSaveGoal = viewModel::updateGoal,
                        modifier = Modifier.fillMaxSize(),
                    )
                }
            }
        }
    }
}
